import React, { createRef, useEffect, useRef, useState } from 'react';
import { Button, Carousel, Col, Divider, Modal, Row, Typography } from 'antd';
import QuickTurnPartStore from 'stores/QuickTurnPartStore';
import { observer } from 'mobx-react-lite';
import { CarouselRef } from 'antd/lib/carousel';
import slide_threeDViewer from 'assets/images/quick-turn/tutorial/slide-threed-viewer.gif';
import slide_materialDropdown from 'assets/images/quick-turn/tutorial/slide-material-dropdown.gif';
import slide_basicDimensions from 'assets/images/quick-turn/tutorial/slide-basic-dimensions.gif';
import slide_quantityDiscount from 'assets/images/quick-turn/tutorial/slide-quantity-discount.gif';

const { Paragraph } = Typography;

const descriptions = [
    {
        key: 0,
        title: '3D Viewer',
        description:
            'Click and drag in the 3D viewer to rotate around your part. Use the "Show Dimensions" toggle to disable on-screen dimensions if desired.',
        img: slide_threeDViewer,
        alt: '3D Viewer Demo',
    },
    {
        key: 1,
        title: 'Choose Your Material',
        description:
            'Select your desired Material from the dropdown. We offer a variety of metal and plastic material options to suit your requirements.',
        img: slide_materialDropdown,
        alt: 'Material Selection Demo',
    },
    {
        key: 2,
        title: 'Customize Your Part',
        description:
            'Customize dimensions like the Overall Length of your part, Outer Diameter, and Chamfer Width. Add an optional Thru-hole, or Flange, or Countersink',
        img: slide_basicDimensions,
        alt: 'Customize Dimensions Demo',
    },
    {
        key: 3,
        title: 'Instant Pricing',
        description:
            'See instant pricing for your part, along with volume discounts when you purchase quantities of two or more.',
        img: slide_quantityDiscount,
        alt: 'Quantity Discount Demo',
    },
];

const WelcomeModal = () => {
    const onChange = (currentSlide: number) => {
        setCurrentSlide(currentSlide);
    };

    function onCloseHandler() {
        QuickTurnPartStore.setTourModalVisible(false);
        setCurrentSlide(0);
        carousel?.current?.goTo(0);
    }

    const [currentSlide, setCurrentSlide] = useState<number>(0);

    const carousel = createRef<CarouselRef>();

    useEffect(() => {
        const timer = setTimeout(() => {
            if (!localStorage.getItem('tour_modal_dismissed')) {
                QuickTurnPartStore.setTourModalVisible(true);
            }
        }, 1500);

        return () => clearTimeout(timer);
    }, []);

    return (
        <Modal
            open={QuickTurnPartStore.tourModalVisible}
            footer={null}
            width="960px"
            style={{ marginTop: '0!important', marginLeft: 'auto', marginRight: 'auto', minWidth: '360px' }}
            bodyStyle={{ padding: 0 }}
            onCancel={onCloseHandler}
            closable={false}
            destroyOnClose
        >
            <Row className="m-0">
                <Col md={8} span={24}>
                    <div className="d-flex flex-column h-100 p-md-4 p-3">
                        <span className="font-size-xxl font-weight-bold">Feature Tour</span>
                        <div className="d-md-flex flex-column h-100 align-items-md-center d-none">
                            <Divider />
                            <div className="d-flex flex-column">
                                <span className="font-size-lg font-weight-med mb-3">
                                    {descriptions.find(x => x.key === currentSlide)?.title}
                                </span>
                                <Paragraph className="font-size-base mb-0">
                                    {descriptions.find(x => x.key === currentSlide)?.description}
                                </Paragraph>
                            </div>
                        </div>
                    </div>
                </Col>
                <Col md={16} span={24} className="p-md-4 p-1">
                    <Carousel afterChange={onChange} ref={carousel}>
                        {descriptions.map(slide => (
                            <img key={slide.key} src={slide.img} alt={slide.alt} loading="lazy" />
                        ))}
                    </Carousel>
                </Col>
            </Row>
            <div className="d-md-none d-flex flex-column h-100 align-items-md-center p-md-0 p-3">
                <div className="d-flex flex-column">
                    <span className="font-size-lg font-weight-med mb-3">
                        {descriptions.find(x => x.key === currentSlide)?.title}
                    </span>
                    <Paragraph className="font-size-base mb-0">
                        {descriptions.find(x => x.key === currentSlide)?.description}
                    </Paragraph>
                </div>
            </div>
            <Divider className="m-0 p-0" />
            <div className="d-flex justify-content-between p-3">
                <div>
                    <Button type="ghost" onClick={onCloseHandler}>
                        Dismiss
                    </Button>
                </div>
                <div className="d-flex" style={{ columnGap: '6px' }}>
                    <Button type="ghost" onClick={() => carousel?.current?.prev()}>
                        Previous
                    </Button>
                    <Button
                        type="primary"
                        onClick={() => {
                            if (currentSlide === descriptions.length - 1) {
                                QuickTurnPartStore.setTourModalVisible(false);
                            } else {
                                carousel?.current?.next();
                            }
                        }}
                    >
                        {currentSlide === descriptions.length - 1 ? "Let's Go!" : 'Next'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default observer(WelcomeModal);
